'use client';

import { useState } from 'react';
import { Box, FormControlLabel, Menu, MenuItem, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { GridPreferencePanelsValue } from '@mui/x-data-grid-pro';
import { SEARCH_TYPE } from '@/constants/searchType';
import { RESULT_DISPLAY_TYPE } from '@/constants/resultDisplay';
import BreedplanInterpretationInfoModal from '@/components/publicSearch/animal/ebv/BreedplanInterpretationInfoModal';
import {
  formatEbvAccuracyLabel,
  formatEbvPluralLabel,
} from '@/components/publicSearch/animal/ebv/ebvProviderCapabilities';
import { useAnimalDisplayType } from '@/hooks/publicSearch/animal/useAnimalDisplayType';
import { useEbvDisplayOptions } from '@/hooks/publicSearch/useEbvDisplayOptions';
import { useIsEbvEnabled } from '@/hooks/publicSearch/useIsEbvEnabled';
import { useSearchType } from '@/hooks/publicSearch/useSearchType';
import useGridStore from '@/publicSearch/store/useGridStore';
import { searchApiRef } from '../../searchSection/PublicSearchDataGrid';
import { StyledSearchIconButton } from '../../searchPage/SearchActions';
import CompactSearchSwitch from './CompactSearchSwitch';
import {
  searchControlIconButtonSx,
  searchControlMenuDivider,
  searchControlMenuItemSx,
  searchControlToggleListSx,
} from './searchControlStyles';

export default function SearchDisplayOptionsControl() {
  const searchType = useSearchType();
  const { resultDisplay } = useAnimalDisplayType();
  const isEbvEnabled = useIsEbvEnabled();
  const { ebvLabel, showAccuracy, showPercentileSearch, showBreedplanHelp } =
    useEbvDisplayOptions();
  const {
    resetColumnOrder,
    setEbvCategoryVisibility,
    setEbvColumnVisibility,
    setManualColumnView,
    showEBVs,
    showEBVAccuracy,
    showPercentiles,
    setShowEBVs,
    setShowEBVAccuracy,
    setShowPercentiles,
  } = useGridStore();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [breedplanHelpOpen, setBreedplanHelpOpen] = useState<boolean>(false);

  const open = Boolean(anchorEl);
  const isCardView = resultDisplay === RESULT_DISPLAY_TYPE.GALLERY;
  const showAnimalToggles = searchType === SEARCH_TYPE.ANIMAL;
  const showDetailToggles =
    searchType === SEARCH_TYPE.SALE_CATALOGUES_DETAIL_ANIMALS ||
    searchType === SEARCH_TYPE.SEMEN_CATALOGUES_DETAIL_ANIMALS;
  const showToggleItems =
    showAnimalToggles || (showDetailToggles && !isCardView);
  const closeMenu = () => setAnchorEl(null);

  const resetLayout = () => {
    closeMenu();
    resetColumnOrder?.();
    setEbvCategoryVisibility({});
    setEbvColumnVisibility({});
    setManualColumnView(false);
  };

  return (
    <>
      <Tooltip title="Sort and display options" placement="bottom">
        <StyledSearchIconButton
          sx={searchControlIconButtonSx(open)}
          onClick={(event) => setAnchorEl(event.currentTarget)}
          aria-label="Sort and display options"
          data-dd-action-name="Display: Open Menu"
        >
          <TuneOutlinedIcon color="primary" sx={{ fontSize: 20 }} />
        </StyledSearchIconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        open={open}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClose={closeMenu}
        slotProps={{
          list: showToggleItems ? { sx: searchControlToggleListSx } : undefined,
        }}
      >
        {!isCardView && (
          <MenuItem
            data-dd-action-name="Display: Manage Columns"
            onClick={() => {
              closeMenu();
              searchApiRef.current?.showPreferences(
                GridPreferencePanelsValue.columns,
              );
            }}
            sx={{
              ...searchControlMenuItemSx,
              borderBottom: searchControlMenuDivider,
            }}
          >
            Manage columns
          </MenuItem>
        )}
        {isEbvEnabled &&
          showToggleItems && [
            <MenuItem
              key="display-ebvs"
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
              sx={{ borderBottom: searchControlMenuDivider }}
            >
              <FormControlLabel
                control={
                  <CompactSearchSwitch
                    data-dd-action-name="Display: EBVs Toggle"
                    checked={showEBVs}
                    onChange={(event) => setShowEBVs(event.target.checked)}
                  />
                }
                label={formatEbvPluralLabel(ebvLabel)}
              />
            </MenuItem>,
            showAccuracy && (
              <MenuItem
                key="display-ebv-accuracy"
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
                sx={{ borderBottom: searchControlMenuDivider }}
              >
                <FormControlLabel
                  control={
                    <CompactSearchSwitch
                      data-dd-action-name="Display: EBV Accuracy Toggle"
                      checked={showEBVAccuracy}
                      onChange={(event) =>
                        setShowEBVAccuracy(event.target.checked)
                      }
                    />
                  }
                  label={formatEbvAccuracyLabel(ebvLabel)}
                />
              </MenuItem>
            ),
            showPercentileSearch && (
              <MenuItem
                key="display-percentiles"
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
                sx={{ borderBottom: searchControlMenuDivider }}
              >
                <FormControlLabel
                  control={
                    <CompactSearchSwitch
                      data-dd-action-name="Display: Percentiles Toggle"
                      checked={showPercentiles}
                      onChange={(event) =>
                        setShowPercentiles(event.target.checked)
                      }
                    />
                  }
                  label="Percentiles"
                />
              </MenuItem>
            ),
          ]}
        {isEbvEnabled &&
          showBreedplanHelp &&
          (showAnimalToggles || showDetailToggles) && (
            <MenuItem
              data-dd-action-name="Display: BREEDPLAN Interpretation Info"
              aria-label="How to interpret and use BREEDPLAN information. Opens explanation dialog."
              onClick={() => {
                closeMenu();
                setBreedplanHelpOpen(true);
              }}
              sx={{
                ...searchControlMenuItemSx,
                borderBottom: searchControlMenuDivider,
              }}
            >
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <InfoOutlinedIcon
                  aria-hidden
                  sx={{ fontSize: 16, color: 'text.secondary' }}
                />
                BREEDPLAN interpretation
              </Box>
            </MenuItem>
          )}
        <MenuItem
          data-dd-action-name="Display: Reset Layout"
          onClick={resetLayout}
          sx={searchControlMenuItemSx}
        >
          Reset Layout
        </MenuItem>
      </Menu>
      <BreedplanInterpretationInfoModal
        open={breedplanHelpOpen}
        onClose={() => setBreedplanHelpOpen(false)}
      />
    </>
  );
}
